import { observer } from 'mobx-react'
import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import rootStore from '../../store/RootStore'
import { reportViewKey } from '../../utils/ReportHelper'
import { SectionListItem } from './SectionListItem'
import ReportUnavailable from './ReportUnavailable'
import usePageScroll from '../../hooks/usePageScroll'
import { Box, Center, Heading, Spinner, Text } from '@chakra-ui/react'

// Loading spinner for the section list
const SectionListSpinner = () => {
  return (
    <Center
      width="100%"
      height="70vh"
      flexDirection="column">
      <Spinner
        size="xl"
        thickness="4px"
        speed="0.8s"
        color="brand.brightBlue"
        mb={4}
      />
      <Text
        color="brand.textDarkBlue"
        fontSize="lg">
        Loading report data...
      </Text>
    </Center>
  )
}

const emptyMessages = {
  search: {
    heading: 'No sections match your search',
    body: 'Try a shorter search term, or clear it to see the whole report.',
  },
  filters: {
    heading: 'No sections match the selected filters',
    body: 'Adjust or clear the filters to see more of this report.',
  },
  noContent: {
    heading: 'This report has nothing to show',
    body: 'It loaded successfully, but there are no sections in it.',
  },
}

/**
 * The list's third state. `reportOrdered` empties legitimately — a search that matches nothing,
 * filters that prune every component, or a report whose payload carries none — and without this
 * all three look exactly like a slow load.
 */
const SectionListEmpty = ({ variant }) => {
  const { heading, body } = emptyMessages[variant]

  return (
    <Center
      width="100%"
      height="70vh">
      <Box
        maxW="30rem"
        textAlign="center">
        <Heading
          as="h2"
          size="md"
          mb={3}
          color="brand.textDarkBlue">
          {heading}
        </Heading>
        <Text color="brand.textBlue">{body}</Text>
      </Box>
    </Center>
  )
}

SectionListEmpty.propTypes = {
  variant: PropTypes.oneOf(Object.keys(emptyMessages)).isRequired,
}

/**
 * `hasReportData` is the honest signal for the third case: the view loaded and the payload carried
 * no components at all, so there is neither a search nor a filter for the reader to undo.
 */
const emptyVariant = (hasReportData, searchText) => {
  if (!hasReportData) return 'noContent'
  return searchText ? 'search' : 'filters'
}

const SectionList = observer(({ reportId, snapshotId }) => {
  const {
    reportStore: {
      reportOrdered,
      reportSearchText,
      hasReportData,
      storeReportById,
      loadingReport,
      loadedKey,
      reportError,
      setCurrentSectionId,
    },
  } = rootStore

  usePageScroll()

  const fetchingRef = useRef(false)
  const viewKey = reportViewKey({ reportId, snapshotId })
  const failed = reportError?.key === viewKey

  useEffect(
    () => {
      ;(async () => {
        // Guard clauses - exit early if we shouldn't fetch
        if (fetchingRef.current) return // Already fetching
        // `loadedKey` is set only on success, so it alone means "this view is loaded". Pairing it
        // with `hasReportData` left a report that loads with no components refetching forever:
        // the stop condition never held, and `loadingReport` toggling re-ran this effect.
        if (loadedKey === viewKey) return // Already showing this exact view
        if (failed) return // This view already failed; loadingReport flipping would retry forever
        if (loadingReport) return // Already loading

        // Happy path - fetch the report (live or the selected snapshot)
        fetchingRef.current = true
        try {
          await storeReportById({ reportId, snapshotId })
        } finally {
          fetchingRef.current = false
        }
      })()
    },
    [storeReportById, reportId, snapshotId, loadingReport, loadedKey, failed, viewKey]
  )

  useEffect(() => {
    if (reportOrdered?.length > 0) {
      // Set the initial currentSectionId based on the first section in the ordered list
      setCurrentSectionId(reportOrdered[0].id)
    }
  }, [reportOrdered, setCurrentSectionId])

  const handleSectionChange = (id, isOpen) => {
    console.log('handleSectionChange', id, isOpen) // eslint-disable-line
    // Update currentSectionId based on user interaction
    if (isOpen) {
      setCurrentSectionId(id)
    }
  }

  if (failed) {
    return <ReportUnavailable kind={reportError.kind} />
  }

  // What is loaded is not what the URL asks for. Without this the previous report's structure and
  // chart data render for real while the new one loads — real Highcharts instances against a
  // dictionary `storeReportById` is about to empty.
  if (loadingReport || loadedKey !== viewKey) {
    return <SectionListSpinner />
  }

  if (!reportOrdered?.length) {
    return (
      <SectionListEmpty
        variant={emptyVariant(hasReportData, reportSearchText)}
      />
    )
  }

  return reportOrdered.map(data => (
    <SectionListItem
      key={data.id}
      data={data}
      isChild={false}
      onChange={handleSectionChange}
      // isOpen={data.id === currentSectionId} // Example to indicate if a section is open
    />
  ))
})

SectionList.propTypes = {}

export default SectionList
